import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { CalendarClock, Plus, Trash2, Pencil, Sparkles, Bell, BellOff } from "lucide-react";
import { toast } from "sonner";

const TYPE_LABEL = {
  lease_end: "Lease end", renewal_notice_deadline: "Renewal notice", inspection: "Inspection", custom: "Custom",
};
const fmt = (d) => (d ? new Date(d + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "");
const daysUntil = (d) => Math.ceil((new Date(d + "T00:00:00") - new Date(new Date().toDateString())) / 86400000);

export function KeyDates({ propertyId }) {
  const [dates, setDates] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // date object or {} for new

  const load = useCallback(async () => {
    try {
      const [d, s] = await Promise.all([
        api.get(`/properties/${propertyId}/key-dates`),
        api.get(`/properties/${propertyId}/key-dates/suggestions`),
      ]);
      setDates(d.data);
      setSuggestions(s.data);
    } catch (e) { console.error("load key dates failed", e); }
    finally { setLoading(false); }
  }, [propertyId]);

  useEffect(() => { load(); }, [load]);

  const addSuggestion = async (sug) => {
    try {
      await api.post(`/properties/${propertyId}/key-dates`, { type: sug.type, label: sug.label, date: sug.date, remind: true });
      toast.success("Added to key dates");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't add."); }
  };

  const remove = async (kid) => {
    try { await api.delete(`/properties/${propertyId}/key-dates/${kid}`); setDates((c) => c.filter((d) => d.id !== kid)); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't remove."); }
  };

  if (loading) return null;
  const sorted = dates.slice().sort((a, b) => a.date.localeCompare(b.date));

  return (
    <div className="mt-4 rounded-2xl border border-white/10 bg-white/5 p-5 text-white" data-testid="key-dates">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <span className="grid place-items-center h-10 w-10 rounded-xl bg-amber-400/15 text-amber-100 shrink-0"><CalendarClock size={20} /></span>
          <div className="min-w-0">
            <h3 className="font-heading font-semibold">Key dates</h3>
            <p className="text-sm text-white/60">Track lease-end, renewal-notice, and inspection dates so nothing sneaks up on you.</p>
          </div>
        </div>
        <Button size="sm" data-testid="key-date-add-btn" onClick={() => setEditing({})}
          className="rounded-xl bg-brand hover:bg-brand-hover text-white shrink-0"><Plus size={15} className="mr-1.5" /> Add date</Button>
      </div>

      {suggestions.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2" data-testid="key-date-suggestions">
          {suggestions.map((s) => (
            <button key={`${s.type}-${s.date}`} data-testid={`key-date-suggestion-${s.type}`} onClick={() => addSuggestion(s)}
              className="inline-flex items-center gap-1.5 rounded-full border border-brand/40 bg-brand/10 text-brand text-xs font-semibold px-3 py-1.5 hover:bg-brand/20 transition-colors">
              <Sparkles size={12} /> Add {s.label} · {fmt(s.date)}
            </button>
          ))}
        </div>
      )}

      {sorted.length === 0 ? (
        <p className="mt-4 text-sm text-white/45" data-testid="key-dates-empty">No key dates yet.</p>
      ) : (
        <div className="mt-4 space-y-2">
          {sorted.map((d) => {
            const du = daysUntil(d.date);
            const past = du < 0;
            return (
              <div key={d.id} data-testid={`key-date-${d.id}`} className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/5 px-3.5 py-2.5">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold">{d.label}</p>
                  <p className="text-[11px] text-white/50">
                    {TYPE_LABEL[d.type] || "Date"} · {fmt(d.date)}
                    {!past && <span className="text-amber-100"> · in {du} day{du === 1 ? "" : "s"}</span>}
                    {past && <span className="text-white/35"> · passed</span>}
                  </p>
                </div>
                {d.remind ? <Bell size={14} className="text-emerald-300 shrink-0" title="Reminder on" /> : <BellOff size={14} className="text-white/30 shrink-0" title="Reminder off" />}
                <Button size="sm" variant="ghost" data-testid={`key-date-edit-${d.id}`} onClick={() => setEditing(d)} className="rounded-lg h-8 w-8 p-0 text-white/70 hover:bg-white/10"><Pencil size={13} /></Button>
                <Button size="sm" variant="ghost" data-testid={`key-date-delete-${d.id}`} onClick={() => remove(d.id)} className="rounded-lg h-8 w-8 p-0 text-[#FCA5A5] hover:bg-[#7F1D1D]/40"><Trash2 size={13} /></Button>
              </div>
            );
          })}
        </div>
      )}

      {editing && <KeyDateDialog propertyId={propertyId} existing={editing.id ? editing : null} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function KeyDateDialog({ propertyId, existing, onClose, onSaved }) {
  const [type, setType] = useState(existing?.type || "custom");
  const [label, setLabel] = useState(existing?.label || "");
  const [dateVal, setDateVal] = useState(existing?.date || "");
  const [remind, setRemind] = useState(existing ? existing.remind : true);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!label.trim()) { toast.error("Add a label."); return; }
    if (!dateVal) { toast.error("Pick a date."); return; }
    setSaving(true);
    try {
      const body = { type, label: label.trim(), date: dateVal, remind };
      if (existing) await api.patch(`/properties/${propertyId}/key-dates/${existing.id}`, body);
      else await api.post(`/properties/${propertyId}/key-dates`, body);
      toast.success(existing ? "Date updated" : "Date added");
      onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || "Couldn't save."); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="key-date-dialog">
        <DialogHeader>
          <DialogTitle>{existing ? "Edit key date" : "Add a key date"}</DialogTitle>
          <DialogDescription>These surface on your dashboard so you don't miss them.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Type</Label>
              <select data-testid="key-date-type" value={type} onChange={(e) => setType(e.target.value)}
                className="mt-1 h-10 w-full rounded-xl border border-input bg-background px-3 text-sm">
                <option value="lease_end">Lease end</option>
                <option value="renewal_notice_deadline">Renewal notice</option>
                <option value="inspection">Inspection</option>
                <option value="custom">Custom</option>
              </select>
            </div>
            <div>
              <Label className="text-xs">Date</Label>
              <Input data-testid="key-date-date" type="date" value={dateVal} onChange={(e) => setDateVal(e.target.value)} className="mt-1 h-10 rounded-xl" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Label</Label>
            <Input data-testid="key-date-label" value={label} onChange={(e) => setLabel(e.target.value)} className="mt-1 h-10 rounded-xl" placeholder="e.g. Give notice to renew" />
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" data-testid="key-date-remind" checked={remind} onChange={(e) => setRemind(e.target.checked)} className="h-4 w-4 rounded" />
            Surface this on my dashboard
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-xl" data-testid="key-date-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving} className="rounded-xl bg-brand hover:bg-brand-hover text-white" data-testid="key-date-save">
            {saving ? "Saving…" : existing ? "Save" : "Add date"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
